import Component from '@glimmer/component'
import { guidFor } from '@ember/object/internals'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import { on } from '@ember/modifier'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'

interface Args {
  activeLabel?: string
  inactiveLabel?: string
  value: boolean
  onToggle: () => void
  themeKey?: ThemeKeys
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

export class UIToggle extends Component<Signature> {
  @service intl!: IntlService

  /* While having an empty label/toggle is fine for visual users, for those using screen readers we still need to have
   * default text for on/off
   */
  get ariaLabel() {
    return this.args.value ? this.activeLabel : this.inactiveLabel
  }

  get componentId() {
    return guidFor(this)
  }

  get uid() {
    return `${this.componentId}-ui-toggle`
  }

  get dataCheckedLabel() {
    return this.args.activeLabel || this.emptyLabel
  }

  get dataUncheckedLabel() {
    return this.args.inactiveLabel || this.emptyLabel
  }

  private get activeLabel() {
    return this.args.activeLabel || this.intl.t('toggleOn')
  }

  private readonly emptyLabel = '\u00A0'

  private get inactiveLabel() {
    return this.args.inactiveLabel || this.intl.t('toggleOff')
  }

  get themeClasses() {
    switch (this.args.themeKey) {
      case ThemeKeys.blue:
        return 'peer-checked:bg-oceany-blue-300 peer-checked:[&>div]:text-oceany-blue-300 peer-checked:peer-focus:bg-oceany-blue-350 peer-focus:outline-oceany-blue-300'
      case ThemeKeys.green:
        return 'peer-checked:bg-ms-green-300 peer-checked:[&>div]:text-ms-green-300 peer-focus:outline-ms-green-300 peer-checked:peer-focus:bg-ms-green-350'
      default:
        return 'peer-checked:bg-oceany-blue-300 peer-checked:[&>div]:text-oceany-blue-300 peer-checked:peer-focus:bg-oceany-blue-350 peer-focus:outline-oceany-blue-300'
    }
  }

  <template>
    <div data-test-ui-toggle class="relative w-24" ...attributes>
      <input
        data-test-ui-toggle-input
        id={{this.uid}}
        type="checkbox"
        checked={{@value}}
        {{on "input" @onToggle}}
        class="peer absolute opacity-0"
      />
      <label
        for={{this.uid}}
        aria-label={{this.ariaLabel}}
        class="bg-dusty-black-100 peer-hover:[&>div]:bg-dusty-black-50 peer-focus:[&>div]:bg-dusty-black-50 flex cursor-pointer items-center rounded py-0.5 transition-all peer-focus:outline-2 peer-focus:outline-solid peer-disabled:pointer-events-none peer-disabled:opacity-40 peer-checked:[&>div]:translate-x-full peer-checked:[&>div]:after:content-[attr(data-checked)]
          {{this.themeClasses}}"
      >
        <div
          data-test-ui-toggle-switch
          class="text-dusty-black-300 z-5 mx-0.5 w-[calc(50%-2px)] rounded-xs bg-white text-center leading-6 transition-transform duration-300 ease-in-out after:h-full after:text-xs after:uppercase after:content-[attr(data-unchecked)]"
          data-checked={{this.dataCheckedLabel}}
          data-unchecked={{this.dataUncheckedLabel}}
          data-test-ui-toggle-value="{{@value}}"
        ></div>
      </label>
    </div>
  </template>
}

export default UIToggle
